/**
 * reviewTypes — the payment-review wire types (BON-381).
 *
 * Mirrored VERBATIM from §2.2 of the frozen BON-381 contract, which the API
 * slice implements from the same text. Field names and nullability are the
 * contract: if one of these looks wrong, the contract changes, not this file.
 */

export type ReviewKind = 'booking' | 'checkout';

/**
 * Which of the three states an item is in — computed by the API and sent, NOT
 * derived here.
 *
 * Deriving it locally is what caused the bug this field exists to prevent: the
 * API read three states out of `needsReview` + `reviewedAt`, this layer read two
 * (`!needsReview`), and the two agreed that a `superseded` item was "resolved" —
 * so the flag card credited a resolution to a person who never touched it. Read
 * `state`. Never re-derive it from the raw columns.
 *
 *   open        an operator still needs to deal with it
 *   resolved    a named human cleared it, with a note
 *   superseded  it left the queue with NO human involved (fulfillment created the
 *               booking that now carries the flag). There is nobody to name.
 */
export type ReviewState = 'open' | 'resolved' | 'superseded';

/**
 * What KIND of problem an item is — the machine-readable axis beside the
 * free-text reason, and what the queue filters on.
 *
 * Mirrored from `backend/src/services/payment-review.categories.ts`. Coarse on
 * purpose: several reasons share one category, because a category answers "what
 * sort of work is this" rather than "what exactly happened". One item can carry
 * several — a booking that is over capacity AND whose hold lapsed is both.
 */
export type ReviewCategory =
  | 'capacity'
  | 'seat_hold'
  | 'timing'
  | 'fulfillment'
  | 'notification'
  | 'payment_stalled'
  | 'gift_pass';

/** Filter-chip order and copy. The order is rough triage urgency: money taken
 *  with no booking first, a customer we cannot reach last. */
export const REVIEW_CATEGORY_OPTIONS: { value: ReviewCategory; label: string }[] = [
  { value: 'fulfillment', label: 'No booking' },
  { value: 'capacity', label: 'Over capacity' },
  { value: 'seat_hold', label: 'Seat hold' },
  { value: 'timing', label: 'Too late' },
  { value: 'notification', label: 'Unreachable' },
  { value: 'payment_stalled', label: 'Payment stalled' },
  { value: 'gift_pass', label: 'Gift pass unusable' },
];

export interface ReviewQueueItem {
  kind: ReviewKind;
  /** The entity's own uuid. This is the key in every route path. */
  id: string;
  /** Human-facing handle: a booking's confirmationCode when it has one, else a
   *  shortened id. There is deliberately NO synthetic "RVW-####" identifier. */
  reference: string;
  /** The one authority on this item's state. See `ReviewState`. */
  state: ReviewState;
  needsReview: boolean;
  /** Free text, possibly several reasons joined with "; ". Never an enum. */
  reviewReason: string | null;
  /** What kind of problem this is. Never empty on a flagged item; several
   *  possible, because a row can genuinely be two things. */
  categories: ReviewCategory[];
  /** ISO instant, read from the row's own `flaggedAt` column — the queue's
   *  ORDER BY key, which is why it is stored rather than derived. */
  flaggedAt: string;
  /** Whole days elapsed since flaggedAt. Required by the AC ("age in days"). */
  ageDays: number;
  reviewedAt: string | null;
  reviewedBy: { id: string; name: string } | null;
  customer: { name: string | null; email: string | null };
  /** Null on a gift-pass purchase checkout: it was for passes, not a workshop. */
  workshop: {
    id: string;
    name: string;
    startsAt: string;
    localStartDatetime: string | null;
    venueName: string | null;
    city: string | null;
    state: string | null;
    regionId: string | null;
  } | null;
  /** Seats on a booking or workshop checkout; passes on a gift-pass purchase. */
  ticketCount: number;
  /** The order total we recorded — the persisted quote. Never recomputed. */
  totalCents: number;
  /** Included in `totalCents`; 0 when none was charged. */
  serviceFeeCents: number;
  currency: string;
  /** Bookings only; null on checkouts. */
  amountPaidCents: number | null;
  /** Cash this booking returned as STORE CREDIT rather than to a card. Stripe
   *  never sees it, so reconciliation must add it back — the money left the
   *  booking but stayed in the business. 0 when there is none. */
  storeCreditRefundedCents: number;
  paymentIntentId: string | null;
}

export interface ReviewQueueResponse {
  openCount: number;
  resolvedCount: number;
  /** Both arrays are sorted OLDEST FIRST — the ageing signal the pre-mortem asks
   *  for. The design's severity sort is dropped: reasons are free text, so there
   *  is nothing to rank on. The two-section split preserves its intent. */
  bookings: ReviewQueueItem[];
  checkouts: ReviewQueueItem[];
}

export interface ReviewTimelineEntry {
  at: string;
  /** "System", "Stripe", "Customer", or a staff user's name. */
  actor: string;
  label: string;
  detail: string | null;
  /** True renders the design's warn-toned dot instead of the clock icon. */
  flag: boolean;
}

export interface ReviewHistoryEntry {
  id: string;
  /** `superseded` is written when a checkout's flag is handed to the booking that
   *  was created for it — the flag belongs to the row that exists, so one incident
   *  is one queue item. It is never produced by an operator action. */
  action: 'flagged' | 'resolved' | 'reopened' | 'superseded';
  reason: string | null;
  note: string | null;
  at: string;
  actor: { id: string; name: string } | null;
}

export interface ReviewStripeSnapshot {
  paymentIntentId: string;
  /** Stripe's own status string, verbatim. */
  status: string;
  amountCents: number;
  capturedCents: number;
  refundedCents: number;
  /** e.g. "Visa ···· 4242". Null when Stripe reports no payment method. */
  card: string | null;
  dashboardUrl: string;
}

export interface ReviewDetail extends ReviewQueueItem {
  timeline: ReviewTimelineEntry[];
  history: ReviewHistoryEntry[];
  /** Null when no PaymentIntent exists, or when the Stripe read failed. */
  stripe: ReviewStripeSnapshot | null;
  /** True ONLY when a PaymentIntent exists but Stripe could not be reached. The
   *  page must still render; money detail degrades, the page does not fail. */
  stripeUnavailable: boolean;
}

/** `?resolved=` on `GET /api/v2/payment-reviews` (default `exclude`). */
export type ReviewResolvedFilter = 'exclude' | 'include' | 'only';
